import { useState, useRef, useEffect, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import type { User } from "@/services/users";
import { getRoleColor, rolePermissionMap, sortRoles } from "@/constants/roleColors";
import { ADMIN_ROLE } from "@/utils/user";
import { calculatePopoverPosition } from "./UsersTable.position";

const ROOT_USER_ID = "11111111-1111-1111-1111-111111111000";
const MAX_COLLAPSED_STUDY_HEIGHT = 56;
const USER_SCOPE_BADGE_CLASS =
  "inline-flex items-center rounded px-2 py-0.5 text-[11px] font-medium whitespace-nowrap";

// Source 列各来源标签的展示文案与配色。
const USER_SOURCE_BADGES: Record<
  string,
  { label: string; bg: string; color: string; border: string }
> = {
  sso: {
    label: "SSO",
    bg: "rgba(100,116,139,0.08)",
    color: "#64748b",
    border: "rgba(100,116,139,0.15)",
  },
  manual: {
    label: "Manual",
    bg: "rgba(131,0,81,0.06)",
    color: "#830051",
    border: "rgba(131,0,81,0.15)",
  },
  dg: {
    label: "DG",
    bg: "rgba(13,148,136,0.08)",
    color: "#0f766e",
    border: "rgba(13,148,136,0.18)",
  },
};

function UserSourceBadges({ sources }: { sources: string[] }) {
  if (sources.length === 0) return <span className="text-gray-400">—</span>;
  return (
    <div className="flex flex-wrap items-center gap-1">
      {sources.map((source) => {
        const badge = USER_SOURCE_BADGES[source] ?? {
          label: source,
          bg: "rgba(100,116,139,0.08)",
          color: "#64748b",
          border: "rgba(100,116,139,0.15)",
        };
        return (
          <span
            key={source}
            className={USER_SCOPE_BADGE_CLASS}
            style={{
              backgroundColor: badge.bg,
              color: badge.color,
              border: `1px solid ${badge.border}`,
            }}
          >
            {badge.label}
          </span>
        );
      })}
    </div>
  );
}

function useAnchoredPopoverPosition(anchorRef: React.RefObject<HTMLButtonElement | null>) {
  const popoverRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);

  useLayoutEffect(() => {
    const updatePosition = () => {
      const anchor = anchorRef.current;
      const popover = popoverRef.current;
      if (!anchor || !popover) return;

      setPosition(
        calculatePopoverPosition(anchor.getBoundingClientRect(), popover.getBoundingClientRect(), {
          width: window.innerWidth,
          height: window.innerHeight,
        }),
      );
    };

    updatePosition();
    // 捕获所有滚动容器的 scroll，保持 Portal 中的 fixed 浮层始终贴着按钮。
    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);
    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [anchorRef]);

  return { popoverRef, position };
}

interface UsersTableProps {
  users: User[];
  /** 分类 ID → 名称。列表只存 ID，展示时现查，分类改名后自动跟随 */
  categoryNameById: Record<string, string>;
  /** 动态授予全部用户的分类 ID；不要求重复写入每个用户的 DocCategoryIds */
  visibleToAllCategoryIds?: string[];
  loading?: boolean;
  onDisable: (id: string) => void;
  onEditUser: (user: User) => void;
  onDeleteUser?: (id: string) => void;
  statusActionUserId?: string | null;
  deleteActionUserId?: string | null;
}

function StudyCodesCell({ items = [] }: { items?: string[] }) {
  const [expanded, setExpanded] = useState(false);
  const [hiddenCount, setHiddenCount] = useState(0);
  const wrapRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const el = wrapRef.current;
    if (!el) return;

    const measure = () => {
      el.style.maxHeight = "none";
      const fullHeight = el.scrollHeight;
      el.style.maxHeight = expanded ? "none" : `${MAX_COLLAPSED_STUDY_HEIGHT}px`;

      if (fullHeight <= MAX_COLLAPSED_STUDY_HEIGHT + 2) {
        setHiddenCount(0);
        return;
      }

      const containerTop = el.getBoundingClientRect().top;
      const hidden = Array.from(el.children).filter((tag) => {
        const tagBottom = (tag as HTMLElement).getBoundingClientRect().bottom - containerTop;
        return tagBottom > MAX_COLLAPSED_STUDY_HEIGHT + 2;
      }).length;
      setHiddenCount(hidden);
    };

    measure();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [expanded, items]);

  if (items.length === 0) {
    return <span className="text-xs text-gray-400">-</span>;
  }

  const showToggle = expanded || hiddenCount > 0;

  return (
    <div>
      <div
        ref={wrapRef}
        className="flex flex-col items-start gap-y-1.5 overflow-hidden transition-all duration-200"
        style={{ maxHeight: expanded ? "none" : `${MAX_COLLAPSED_STUDY_HEIGHT}px` }}
      >
        {items.map((item) => (
          <span
            key={item}
            className="rounded px-2 py-0.5 text-xs font-medium whitespace-nowrap"
            style={{
              backgroundColor: "rgba(131,0,81,0.06)",
              color: "#830051",
              border: "1px solid rgba(131,0,81,0.15)",
            }}
          >
            {item}
          </span>
        ))}
      </div>
      {showToggle && (
        <button
          type="button"
          onClick={(event) => {
            event.stopPropagation();
            setExpanded((value) => !value);
          }}
          className="mt-1.5 flex cursor-pointer select-none items-center gap-0.5 text-[11px] whitespace-nowrap transition-opacity hover:opacity-70"
          style={{ color: "#830051" }}
        >
          <i className={`${expanded ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} text-sm`} />
          {expanded ? "Show Less" : `+${hiddenCount} more`}
        </button>
      )}
    </div>
  );
}

function GeneralSourceTypeCell({ items }: { items: string[] }) {
  if (items.length === 0) {
    return <span className="text-xs text-gray-400">-</span>;
  }

  return (
    <div className="flex flex-wrap gap-x-2.5 gap-y-1.5">
      {items.map((item) => (
        <span
          key={item}
          className={USER_SCOPE_BADGE_CLASS}
          style={{
            backgroundColor: "rgba(131,0,81,0.06)",
            color: "#830051",
            border: "1px solid rgba(131,0,81,0.15)",
          }}
        >
          {item}
        </span>
      ))}
    </div>
  );
}

export function UserRegionCell({ region }: { region: User["region"] }) {
  const isHk = region === "HK";
  return (
    <span
      className={USER_SCOPE_BADGE_CLASS}
      style={{
        backgroundColor: isHk ? "rgba(37,99,235,0.06)" : "rgba(71,85,105,0.07)",
        color: isHk ? "#2563eb" : "#475569",
        border: `1px solid ${isHk ? "rgba(37,99,235,0.16)" : "rgba(71,85,105,0.14)"}`,
      }}
    >
      {region}
    </span>
  );
}

function DisableConfirmPopover({
  user,
  anchorRef,
  onConfirm,
  onCancel,
  loading = false,
}: {
  user: User;
  anchorRef: React.RefObject<HTMLButtonElement | null>;
  onConfirm: () => void;
  onCancel: () => void;
  loading?: boolean;
}) {
  const isDisabled = user.status === "Disabled";
  const { popoverRef, position } = useAnchoredPopoverPosition(anchorRef);

  return createPortal(
    <div
      ref={popoverRef}
      role="dialog"
      aria-label={isDisabled ? "Enable User" : "Disable User"}
      className="fixed z-[9999] w-64 bg-white border border-gray-200 rounded-xl p-4"
      style={{
        boxShadow: "0 4px 20px rgba(0,0,0,0.12)",
        top: position?.top ?? 0,
        left: position?.left ?? 0,
        visibility: position ? "visible" : "hidden",
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <p className="text-xs font-semibold text-gray-800 mb-1">
        {isDisabled ? "Enable User" : "Disable User"}
      </p>
      <p className="text-xs text-gray-500 leading-relaxed mb-3">
        {isDisabled
          ? `${user.name} will be re-enabled and can log in again.`
          : `${user.name} will be disabled and lose access to all features.`}
      </p>
      <div className="flex items-center justify-end gap-2">
        <button
          onClick={onCancel}
          disabled={loading}
          className="text-xs px-3 py-1.5 rounded-lg border border-gray-200 text-gray-500 cursor-pointer hover:bg-gray-50 whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Cancel
        </button>
        <button
          onClick={onConfirm}
          disabled={loading}
          className="text-xs px-3 py-1.5 rounded-lg text-white cursor-pointer whitespace-nowrap disabled:opacity-70 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
          style={{ backgroundColor: isDisabled ? "#16a34a" : "#dc2626" }}
        >
          {loading && <i className="ri-loader-4-line animate-spin text-xs"></i>}
          {isDisabled ? "Enable" : "Disable"}
        </button>
      </div>
    </div>,
    document.body,
  );
}

function DeleteConfirmPopover({
  user,
  anchorRef,
  onConfirm,
  onCancel,
  loading = false,
}: {
  user: User;
  anchorRef: React.RefObject<HTMLButtonElement | null>;
  onConfirm: () => void;
  onCancel: () => void;
  loading?: boolean;
}) {
  const { popoverRef, position } = useAnchoredPopoverPosition(anchorRef);

  return createPortal(
    <div
      ref={popoverRef}
      role="dialog"
      aria-label="Delete User"
      className="fixed z-[9999] w-64 bg-white border border-gray-200 rounded-xl p-4"
      style={{
        boxShadow: "0 4px 20px rgba(0,0,0,0.12)",
        top: position?.top ?? 0,
        left: position?.left ?? 0,
        visibility: position ? "visible" : "hidden",
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <p className="text-xs font-semibold text-gray-800 mb-1">Delete User</p>
      <p className="text-xs text-gray-500 leading-relaxed mb-3">
        {user.name} will be permanently removed along with their role assignments. This action
        cannot be undone.
      </p>
      <div className="flex items-center justify-end gap-2">
        <button
          onClick={onCancel}
          disabled={loading}
          className="text-xs px-3 py-1.5 rounded-lg border border-gray-200 text-gray-500 cursor-pointer hover:bg-gray-50 whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Cancel
        </button>
        <button
          onClick={onConfirm}
          disabled={loading}
          className="text-xs px-3 py-1.5 rounded-lg text-white cursor-pointer whitespace-nowrap disabled:opacity-70 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
          style={{ backgroundColor: "#dc2626" }}
        >
          {loading && <i className="ri-loader-4-line animate-spin text-xs"></i>}
          Delete
        </button>
      </div>
    </div>,
    document.body,
  );
}

export default function UsersTable({
  users,
  categoryNameById,
  visibleToAllCategoryIds = [],
  loading = false,
  onDisable,
  onEditUser,
  onDeleteUser,
  statusActionUserId,
  deleteActionUserId,
}: UsersTableProps) {
  const { t } = useTranslation();
  const [confirmDisableId, setConfirmDisableId] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const btnRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const deleteBtnRefs = useRef<Record<string, HTMLButtonElement | null>>({});
  const prevStatusActionUserId = useRef<string | null>(null);
  const prevDeleteActionUserId = useRef<string | null>(null);

  useEffect(() => {
    if (prevStatusActionUserId.current && !statusActionUserId) {
      setConfirmDisableId(null);
    }
    prevStatusActionUserId.current = statusActionUserId ?? null;
  }, [statusActionUserId]);

  useEffect(() => {
    if (prevDeleteActionUserId.current && !deleteActionUserId) {
      setConfirmDeleteId(null);
    }
    prevDeleteActionUserId.current = deleteActionUserId ?? null;
  }, [deleteActionUserId]);

  if (!loading && users.length === 0) {
    return (
      <div className="bg-white border border-gray-100 rounded-xl flex flex-col items-center justify-center py-20 text-center">
        <span className="w-10 h-10 flex items-center justify-center mb-3">
          <i className="ri-user-search-line text-3xl text-gray-300"></i>
        </span>
        <p className="text-sm font-medium text-gray-400">{t("users.table.noResults")}</p>
        <p className="text-xs text-gray-300 mt-1">{t("users.table.noResultsHint")}</p>
      </div>
    );
  }

  return (
    <div
      className="bg-white border border-gray-100 rounded-xl overflow-hidden"
      onClick={() => {
        setConfirmDisableId(null);
        setConfirmDeleteId(null);
      }}
    >
      <div className="overflow-x-auto">
        <table className="w-full min-w-[1370px] table-fixed font-sans text-sm">
          <colgroup>
            <col style={{ width: 240 }} />
            <col style={{ width: 220 }} />
            <col style={{ width: 100 }} />
            <col style={{ width: 200 }} />
            <col style={{ width: 220 }} />
            <col style={{ width: 160 }} />
            <col style={{ width: 110 }} />
            <col style={{ width: 140 }} />
          </colgroup>
          <thead>
            <tr className="bg-gray-50">
              <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 border-b border-gray-100">
                {t("users.table.user")}
              </th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 border-b border-gray-100">
                Email
              </th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 border-b border-gray-100">
                Region
              </th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 border-b border-gray-100">
                MVR Studies
              </th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 border-b border-gray-100">
                General Source Type
              </th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 border-b border-gray-100">
                {t("users.table.statusLastActive")}
              </th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 border-b border-gray-100">
                Source
              </th>
              <th className="text-left pl-8 px-5 py-3 text-xs font-semibold text-gray-500 border-b border-l border-gray-100 sticky right-0 z-20 bg-gray-50">
                {t("users.table.actions")}
              </th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={8} className="px-6 py-16 text-center text-sm text-gray-400">
                  <i className="ri-loader-4-line text-2xl animate-spin inline-block mb-2"></i>
                  <div>{t("common.loading")}</div>
                </td>
              </tr>
            ) : (
              users.map((user, idx) => {
                const isLast = idx === users.length - 1;
                const isDisabled = user.status === "Disabled";
                const isRootUser = user.id.toLowerCase() === ROOT_USER_ID;
                const isStatusLoading = statusActionUserId === user.id;
                const isDeleteLoading = deleteActionUserId === user.id;
                const hasRowActionLoading = isStatusLoading || isDeleteLoading;
                const showConfirm = confirmDisableId === user.id;
                const showDeleteConfirm = confirmDeleteId === user.id;

                const statusColor =
                  user.status === "Active"
                    ? "#16a34a"
                    : user.status === "Disabled"
                      ? "#dc2626"
                      : "#9ca3af";
                const statusDot =
                  user.status === "Active"
                    ? "#16a34a"
                    : user.status === "Disabled"
                      ? "#dc2626"
                      : "#d1d5db";
                const visibleRoles = user.roles;

                return (
                  <tr
                    key={user.id}
                    className="group hover:bg-gray-50/60 transition-colors"
                    style={{
                      borderBottom: isLast ? "none" : "1px solid #f3f4f6",
                      opacity: isDisabled ? 0.65 : 1,
                    }}
                  >
                    {/* User + Roles */}
                    <td className="px-5 py-4">
                      <div className="text-sm font-medium text-gray-800 leading-snug whitespace-nowrap mb-1">
                        {user.name?.trim() ? (
                          user.name
                        ) : (
                          <span className="text-gray-400 font-normal">-</span>
                        )}
                      </div>
                      {user.kAccount && (
                        <div className="text-[11px] text-gray-400 mb-1 whitespace-nowrap">
                          {user.kAccount}
                        </div>
                      )}

                      {isDisabled && (
                        <div className="text-[10px] text-red-400 mb-1.5 flex items-center gap-1">
                          <i className="ri-lock-line text-[10px]"></i>
                          {t("users.table.noAccess")}
                        </div>
                      )}

                      {/* Roles row（后端不区分内部/外部，统一展示） */}
                      <div className="flex flex-nowrap gap-1 mb-1.5 overflow-x-auto">
                        {visibleRoles.length > 0 ? (
                          sortRoles(visibleRoles).map((role) => {
                            const roleColor = getRoleColor(role);

                            return (
                              <span
                                key={role}
                                className="inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium whitespace-nowrap cursor-help"
                                style={{ backgroundColor: roleColor.bg, color: roleColor.color }}
                                title={rolePermissionMap[role]}
                              >
                                {role}
                              </span>
                            );
                          })
                        ) : (
                          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] text-gray-400 bg-gray-100 whitespace-nowrap">
                            <i className="ri-time-line" style={{ fontSize: "10px" }}></i>
                            {t("users.table.noLocalRole")}
                          </span>
                        )}
                      </div>
                    </td>

                    {/* Email */}
                    <td className="px-4 py-4 align-top">
                      <div className="overflow-hidden text-ellipsis text-xs text-gray-700 whitespace-nowrap">
                        {user.email || "-"}
                      </div>
                    </td>
                    {/* Region */}
                    <td className="px-4 py-4 align-top">
                      <UserRegionCell region={user.region} />
                    </td>
                    {/* MVR Studies */}
                    <td className="px-4 py-4 align-top">
                      <StudyCodesCell items={user.studyCodes} />
                    </td>
                    {/* General Source Type */}
                    <td className="px-4 py-4 align-top">
                      <GeneralSourceTypeCell
                        items={(() => {
                          if (
                            user.roles.some(
                              (role) => role.toLowerCase() === ADMIN_ROLE.toLowerCase(),
                            )
                          ) {
                            return ["All Types"];
                          }
                          // 用户显式授权与全员动态授权合并；已删除分类查不到名称时跳过。
                          return [...new Set([...user.docCategoryIds, ...visibleToAllCategoryIds])]
                            .map((id) => categoryNameById[id])
                            .filter((n): n is string => !!n);
                        })()}
                      />
                    </td>
                    {/* Status + Last Active */}
                    <td className="px-4 py-4 align-top">
                      <div className="inline-flex items-center gap-1.5 mb-1">
                        <span
                          className="w-1.5 h-1.5 rounded-full shrink-0"
                          style={{ backgroundColor: statusDot }}
                        />
                        <span
                          className="text-xs font-medium whitespace-nowrap"
                          style={{ color: statusColor }}
                        >
                          {user.status}
                        </span>
                      </div>
                      <div className="text-[11px] text-gray-400 whitespace-nowrap">
                        {user.lastActive && user.lastActive !== "-"
                          ? user.lastActive
                          : "Never Login"}
                      </div>
                    </td>
                    {/* Source */}
                    <td className="px-4 py-4 align-top">
                      <UserSourceBadges sources={user.sources} />
                    </td>
                    {/* Actions */}
                    <td className="px-5 py-4 align-top sticky right-0 z-10 bg-white border-l border-gray-100 ">
                      <div className="flex items-center justify-end gap-1.5 relative">
                        {/* Edit user */}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            onEditUser(user);
                          }}
                          className="w-7 h-7 flex items-center justify-center rounded-md cursor-pointer hover:bg-gray-100 transition-colors"
                          style={{ color: "#830051" }}
                          title="Edit"
                        >
                          <i className="ri-edit-line text-sm"></i>
                        </button>

                        {/* Disable / Enable */}
                        <button
                          ref={(el) => {
                            btnRefs.current[user.id] = el;
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (isRootUser || hasRowActionLoading) return;
                            setConfirmDisableId(showConfirm ? null : user.id);
                          }}
                          className={`w-7 h-7 flex items-center justify-center rounded-md transition-colors ${
                            isRootUser || hasRowActionLoading
                              ? "opacity-30 cursor-not-allowed"
                              : "cursor-pointer hover:bg-gray-100"
                          }`}
                          style={{ color: isDisabled ? "#16a34a" : "#dc2626" }}
                          title={
                            isRootUser
                              ? "Root user cannot be disabled"
                              : isDisabled
                                ? t("users.table.enable")
                                : t("users.table.disable")
                          }
                          disabled={isRootUser || hasRowActionLoading}
                        >
                          {isStatusLoading ? (
                            <i className="ri-loader-4-line animate-spin text-sm"></i>
                          ) : (
                            <i
                              className={
                                isDisabled
                                  ? "ri-user-follow-line text-sm"
                                  : "ri-forbid-line text-sm"
                              }
                            ></i>
                          )}
                        </button>

                        {showConfirm && (
                          <DisableConfirmPopover
                            user={user}
                            anchorRef={{ current: btnRefs.current[user.id] ?? null }}
                            onConfirm={() => {
                              if (isStatusLoading) return;
                              onDisable(user.id);
                            }}
                            onCancel={() => setConfirmDisableId(null)}
                            loading={isStatusLoading}
                          />
                        )}

                        {/* Delete */}
                        {onDeleteUser && (
                          <button
                            ref={(el) => {
                              deleteBtnRefs.current[user.id] = el;
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (isRootUser || hasRowActionLoading) return;
                              setConfirmDisableId(null);
                              setConfirmDeleteId(showDeleteConfirm ? null : user.id);
                            }}
                            className={`w-7 h-7 flex items-center justify-center rounded-md transition-colors ${
                              isRootUser || hasRowActionLoading
                                ? "opacity-30 cursor-not-allowed"
                                : "cursor-pointer hover:bg-gray-100"
                            }`}
                            style={{ color: "#dc2626" }}
                            title={isRootUser ? "Root user cannot be deleted" : "Delete"}
                            disabled={isRootUser || hasRowActionLoading}
                          >
                            {isDeleteLoading ? (
                              <i className="ri-loader-4-line animate-spin text-sm"></i>
                            ) : (
                              <i className="ri-delete-bin-line text-sm"></i>
                            )}
                          </button>
                        )}

                        {showDeleteConfirm && onDeleteUser && (
                          <DeleteConfirmPopover
                            user={user}
                            anchorRef={{ current: deleteBtnRefs.current[user.id] ?? null }}
                            onConfirm={() => {
                              if (isDeleteLoading) return;
                              onDeleteUser(user.id);
                            }}
                            onCancel={() => setConfirmDeleteId(null)}
                            loading={isDeleteLoading}
                          />
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
